import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import type { IBooking } from '@cnt-workspace/models';
import type { IAddress, ITrip } from '@curbnturf/objects';
import type { IListing } from '../listings/mock-listings.data';
import { createIdbKvStore } from '../storage/idb-kv-store.util';
import { AuthService } from '../auth/auth.service';
import { TripBackendService } from './trip-backend.service';
import { apiTripToPlan } from './trip-backend-mapper';

/** Kinds of stop a guest can add to a trip plan. */
export type TripStopKind = 'private' | 'boondocking' | 'poi' | 'custom';

export interface ITripStop {
  id: string;
  kind: TripStopKind;
  /** Source id when the stop references an existing listing or POI.
   *  `number` for listings (1-80 private, 81-100 boondocking), `string` for POIs.
   *  Undefined for `custom` pin-drop stops. */
  refId?: number | string;
  name: string;
  lat: number;
  lng: number;
  /** Display address. */
  address?: string;
  /** Structured address, when known (e.g. a custom pin placed by address
   *  search). Synced to the route point's address columns; stops without it
   *  sync `address` as the street line instead. */
  addressParts?: IAddress;
  /** Optional photo data-URL or asset path. */
  photo?: string;
  /** Per-stop dates, when planned. Both optional; format YYYY-MM-DD. */
  checkInDate?: string;
  checkOutDate?: string;
  notes?: string;
}

export interface ITripPlan {
  id: string;
  name: string;
  /** Trip-level window. Per-stop dates must fall inside it when both are set. */
  startDate?: string;
  endDate?: string;
  /** Ordered list of stops. First = trip start, last = trip finish. */
  stops: ITripStop[];
  /** Corridor radius (miles) for filtering autocomplete results to candidates
   *  within X miles of the existing route polyline. 0 = no filter. */
  corridorMiles?: number;
  createdAt: string;
  updatedAt: string;
  /** Backend `ITrip.id` once this plan has synced at least once. Unset for
   *  a plan that only exists locally (not yet saved, or the user was never
   *  signed in). */
  remoteId?: number;
}

const TRIP_PLANS_KEY = 'cnt-trip-plans';
const TRIP_PLANS_IDB_KEY = 'trip-planner:plans';
const TRIP_ACTIVE_ID_KEY = 'cnt-trip-active-id';
const TRIP_ACTIVE_ID_IDB_KEY = 'trip-planner:active-id';

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** The `/trip-planner/:id` segment for a plan — the backend trip id once it
 *  has synced (stable across devices for its owner), else the local id. */
export function tripPlanRouteId(plan: Pick<ITripPlan, 'id' | 'remoteId'>): string {
  return plan.remoteId != null ? String(plan.remoteId) : plan.id;
}

/** Default auto-name for a freshly created trip — date-based, e.g.
 *  "Trip — May 26, 2026". Inline-renameable wherever the trip surfaces. */
export function autoTripName(now: Date = new Date()): string {
  return `Trip — ${now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
}

/** Parse 'YYYY-MM-DD' or full ISO into a Date; null on garbage. */
export function parseIsoDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date → 'YYYY-MM-DD' — matches how trip plans store dates. */
export function formatIsoDate(d: Date | null | undefined): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Short human label for a date pill, e.g. "May 27" or "May 27, 2026" when
 *  the year differs from the current year. */
export function shortDateLabel(d: Date | null | undefined, now: Date = new Date()): string {
  if (!d) return '';
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
}

/** Minimal trip payload baked into shareable URLs — strips ids, timestamps,
 *  photo blobs. lat/lng rounded to 5 decimals (~1m precision) to shrink the URL. */
export interface ITripShareV1 {
  v: 1;
  n: string;
  s?: string;
  e?: string;
  c?: number;
  S: Array<{
    k: TripStopKind;
    r?: number | string;
    n: string;
    a: number;
    g: number;
    d?: string;
    i?: string;
    o?: string;
    t?: string;
  }>;
}

/** Encode an ITripPlan into a URL-safe base64 string for sharing. */
export function encodeTripShare(plan: ITripPlan): string {
  const payload: ITripShareV1 = {
    v: 1,
    n: plan.name,
    s: plan.startDate,
    e: plan.endDate,
    c: plan.corridorMiles,
    S: plan.stops.map(s => ({
      k: s.kind,
      r: s.refId,
      n: s.name,
      a: Math.round(s.lat * 1e5) / 1e5,
      g: Math.round(s.lng * 1e5) / 1e5,
      d: s.address,
      i: s.checkInDate,
      o: s.checkOutDate,
      t: s.notes,
    })),
  };
  const json = JSON.stringify(payload);
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decode a share-link payload back into stop data the viewer can render. */
export function decodeTripShare(payload: string): ITripShareV1 | null {
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 ? '='.repeat(4 - (padded.length % 4)) : '';
    const b64 = padded + pad;
    const json = decodeURIComponent(escape(atob(b64)));
    const parsed = JSON.parse(json);
    if (parsed?.v !== 1 || !Array.isArray(parsed?.S)) return null;
    return parsed as ITripShareV1;
  } catch {
    return null;
  }
}

/** Great-circle distance between two points in miles. */
export function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 3959;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Distance (miles) from a point to a line segment, using an equirectangular
 *  projection valid at small scales (well within a continental road trip). */
function pointToSegmentMiles(
  p: { lat: number; lng: number },
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const midLat = ((a.lat + b.lat) / 2) * Math.PI / 180;
  const kx = 69.172 * Math.cos(midLat);
  const ky = 69.172;
  const ax = a.lng * kx, ay = a.lat * ky;
  const bx = b.lng * kx, by = b.lat * ky;
  const px = p.lng * kx, py = p.lat * ky;
  const dx = bx - ax, dy = by - ay;
  if (dx === 0 && dy === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Minimum distance (miles) from a candidate point to any segment of a route
 *  polyline. Used for corridor filtering of autocomplete results. */
export function pointToRouteMiles(
  p: { lat: number; lng: number },
  route: { lat: number; lng: number }[],
): number {
  if (route.length === 0) return Infinity;
  if (route.length === 1) return haversineMiles(p, route[0]);
  let best = Infinity;
  for (let i = 0; i < route.length - 1; i++) {
    const d = pointToSegmentMiles(p, route[i], route[i + 1]);
    if (d < best) best = d;
  }
  return best;
}

/** Douglas–Peucker tolerance (miles) for `corridorPolyline` — fine enough
 *  that corridor distances stay accurate, coarse enough that a cross-country
 *  OSRM line collapses from 10k+ points to a few hundred. */
const CORRIDOR_SIMPLIFY_MILES = 1;

/** Polyline used for corridor filtering: the road route's geometry
 *  (`[lng, lat]` pairs, as OSRM returns it), simplified for speed, when
 *  available; otherwise the stops themselves (straight lines between them). */
export function corridorPolyline(
  stops: readonly { lat: number; lng: number }[],
  routeCoords: readonly [number, number][] | null | undefined,
): { lat: number; lng: number }[] {
  if (!routeCoords || routeCoords.length < 2) return stops.map(s => ({ lat: s.lat, lng: s.lng }));
  const pts = routeCoords.map(([lng, lat]) => ({ lat, lng }));
  // Iterative Douglas–Peucker (a recursive one can blow the stack on long routes).
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const ranges: [number, number][] = [[0, pts.length - 1]];
  while (ranges.length) {
    const [a, b] = ranges.pop()!;
    let maxD = 0, idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = pointToSegmentMiles(pts[i], pts[a], pts[b]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (idx !== -1 && maxD > CORRIDOR_SIMPLIFY_MILES) {
      keep[idx] = 1;
      ranges.push([a, idx], [idx, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

export type RouteInsertPosition =
  | { kind: 'before' }
  | { kind: 'between'; segmentIndex: number }
  | { kind: 'after' };

/** Where a new stop belongs relative to an existing ordered route: prepended
 *  before the first stop, spliced between a pair of consecutive stops, or
 *  appended after the last stop — whichever adds the least travel distance.
 *  "Between" is scored by detour cost (`dist(a,p) + dist(p,b) - dist(a,b)`)
 *  rather than raw proximity to the leg, so a point that's actually beyond
 *  either end of the route loses to prepending/appending instead of always
 *  being forced into whichever leg it happens to sit closest to. Ties prefer
 *  the later option throughout (matches the pre-existing tie-break for
 *  consecutive legs). With fewer than 2 stops there's no leg to compare
 *  against, so this falls back to `after` (append). */
export function nearestRoutePosition(
  p: { lat: number; lng: number },
  stops: readonly { lat: number; lng: number }[],
): RouteInsertPosition {
  if (stops.length < 2) return { kind: 'after' };
  let bestCost = haversineMiles(p, stops[0]);
  let best: RouteInsertPosition = { kind: 'before' };
  const costAppend = haversineMiles(p, stops[stops.length - 1]);
  if (costAppend <= bestCost) { bestCost = costAppend; best = { kind: 'after' }; }
  for (let i = 0; i < stops.length - 1; i++) {
    const detour = haversineMiles(stops[i], p) + haversineMiles(p, stops[i + 1]) - haversineMiles(stops[i], stops[i + 1]);
    if (detour <= bestCost) { bestCost = detour; best = { kind: 'between', segmentIndex: i }; }
  }
  return best;
}

/** Index at which a new stop should be inserted into `stops` per
 *  `nearestRoutePosition` — prepend, append, or splice after the segment's
 *  first stop. With fewer than 2 stops there's no leg to compare against, so
 *  this falls back to append (`stops.length`). */
export function nearestRouteInsertIndex(
  p: { lat: number; lng: number },
  stops: readonly { lat: number; lng: number }[],
): number {
  if (stops.length < 2) return stops.length;
  const pos = nearestRoutePosition(p, stops);
  if (pos.kind === 'before') return 0;
  if (pos.kind === 'after') return stops.length;
  return pos.segmentIndex + 1;
}

/**
 * Find the live booking (if any) that this stop already represents — drives
 * the "Booked ✓" / "Request out" badges on private-listing stops in the
 * planner. Only matches private listings (boondocking and POIs aren't
 * bookable). Prefers a date-overlapping booking when stop dates are set;
 * falls back to the newest active booking on the listing.
 *
 * Returns null when there's no match or the stop isn't a private listing.
 */
export function bookingForStop(
  stop: Pick<ITripStop, 'kind' | 'refId' | 'checkInDate' | 'checkOutDate'>,
  bookings: readonly IBooking[],
): IBooking | null {
  if (stop.kind !== 'private' || typeof stop.refId !== 'number') return null;
  const live = bookings.filter(b =>
    b.listingId === stop.refId && b.status !== 'cancelled' && b.status !== 'declined'
  );
  if (live.length === 0) return null;
  if (stop.checkInDate && stop.checkOutDate) {
    const sStart = new Date(stop.checkInDate + 'T00:00:00').getTime();
    const sEnd = new Date(stop.checkOutDate + 'T00:00:00').getTime();
    const overlap = live.find(b => {
      const bs = new Date(b.dates.start).getTime();
      const be = new Date(b.dates.end).getTime();
      return bs <= sEnd && be >= sStart;
    });
    if (overlap) return overlap;
  }
  return live.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

/**
 * Generic "along the route" filter — returns the candidates within
 * `corridorMiles` of the polyline through `routePoints`, sorted by distance,
 * excluding any that are already an existing stop. A candidate is considered
 * already-added when either its `id` matches a stop's `refId` (the reliable
 * check — coordinates alone can drift, e.g. a private listing's displayed
 * location shifting slightly between an unbooked/approximate view and its
 * exact post-booking location) or, as a fallback for candidates/stops without
 * ids (e.g. custom pins), its lat/lng matches a stop's. `id`/`refId` are
 * compared with `===`, so a listing's numeric id can never collide with a
 * POI's string id. Used by both /search drawer and the focused editor to
 * power the "Suggested stops along your route" panel.
 *
 * Generic so it works for IListing, IPoi, and anything with lat/lng.
 */
export function suggestionsAlongRoute<T extends { lat: number; lng: number; id?: number | string }>(
  candidates: readonly T[],
  routePoints: readonly { lat: number; lng: number }[],
  corridorMiles: number,
  existingStops: readonly { lat: number; lng: number; refId?: number | string }[],
  max = 5,
): T[] {
  if (corridorMiles <= 0 || routePoints.length < 2 || candidates.length === 0) return [];
  const dupeIds = new Set(existingStops.map(s => s.refId).filter(id => id !== undefined));
  const dupeCoords = new Set(existingStops.map(s => `${s.lat.toFixed(4)},${s.lng.toFixed(4)}`));
  // Cheap bbox prefilter so a full-catalog scan only runs the per-segment
  // distance check on candidates that could possibly be in the corridor.
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of routePoints) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  const padLat = corridorMiles / 69;
  // Longitude degrees shrink toward the poles — pad by the widest latitude.
  const cosLat = Math.max(0.01, Math.cos((Math.max(Math.abs(minLat), Math.abs(maxLat)) * Math.PI) / 180));
  const padLng = corridorMiles / (69.172 * cosLat);
  minLat -= padLat; maxLat += padLat; minLng -= padLng; maxLng += padLng;
  const scored: { c: T; d: number }[] = [];
  for (const c of candidates) {
    if (!(c.lat >= minLat && c.lat <= maxLat && c.lng >= minLng && c.lng <= maxLng)) continue;
    if (c.id !== undefined && dupeIds.has(c.id)) continue;
    if (dupeCoords.has(`${c.lat?.toFixed(4)},${c.lng?.toFixed(4)}`)) continue;
    const d = pointToRouteMiles(c, routePoints as { lat: number; lng: number }[]);
    if (d <= corridorMiles) scored.push({ c, d });
  }
  scored.sort((a, b) => a.d - b.d);
  return scored.slice(0, max).map(s => s.c);
}

export interface ITripCost {
  /** Nights across all stops that have both check-in and check-out set. */
  totalNights: number;
  /** Subset of totalNights that fall at private listings (the only paid kind). */
  paidNights: number;
  /** Sum of paidNights × listing.price across matched private stops. */
  totalCost: number;
  /** True when a private stop's refId didn't match any listing — pricing is incomplete. */
  unknownPrice: boolean;
}

/** Estimate trip cost from stop check-in/out dates × matched listing prices.
 *  Boondocking, POIs, and custom pins contribute nights but no cost. */
export function tripCostSummary(plan: Pick<ITripPlan, 'stops'>, listings: readonly IListing[]): ITripCost {
  let totalNights = 0;
  let paidNights = 0;
  let totalCost = 0;
  let unknownPrice = false;
  for (const s of plan.stops) {
    const ci = parseIsoDate(s.checkInDate);
    const co = parseIsoDate(s.checkOutDate);
    if (!ci || !co) continue;
    const nights = Math.max(0, Math.round((co.getTime() - ci.getTime()) / 86_400_000));
    if (nights === 0) continue;
    totalNights += nights;
    if (s.kind !== 'private') continue;
    const listing = listings.find(l => l.id === s.refId && !('agency' in l));
    if (!listing) { unknownPrice = true; continue; }
    const price = (listing as { price?: number }).price ?? 0;
    paidNights += nights;
    totalCost += nights * price;
  }
  return { totalNights, paidNights, totalCost, unknownPrice };
}

/** A leg longer than this (in minutes) is flagged as a "long drive" — the
 *  default ~6h matches federal commercial-driver fatigue guidance and is a
 *  reasonable single-day RV ceiling. Future: per-RV-profile override. */
const LONG_LEG_MINUTES = 360;

/** National-average regular gas price (USD/gal) used when the user hasn't
 *  overridden the estimate. Refresh periodically; future: live AAA feed. */
const DEFAULT_GAS_PRICE_PER_GALLON = 3.75;

export interface ITripFuel {
  /** Total fuel needed for the whole trip at the given MPG. */
  gallons: number;
  /** Estimated dollar cost at the given gas price. */
  cost: number;
  /** Indices of legs that exceed the RV's tank range — would need a refuel mid-leg. */
  legsOverRange: number[];
}

/** Estimate fuel use, cost, and per-leg range warnings for a trip. Returns
 *  null when MPG is missing or zero — the caller renders a "set MPG" hint. */
export function tripFuelEstimate(
  totalMiles: number,
  legsMiles: number[],
  mpg: number | null | undefined,
  tankGallons: number | null | undefined,
  gasPricePerGallon = DEFAULT_GAS_PRICE_PER_GALLON,
): ITripFuel | null {
  if (!mpg || mpg <= 0) return null;
  const gallons = totalMiles / mpg;
  const cost = gallons * gasPricePerGallon;
  const range = tankGallons && tankGallons > 0 ? tankGallons * mpg : Infinity;
  const legsOverRange: number[] = [];
  legsMiles.forEach((mi, i) => { if (mi > range) legsOverRange.push(i); });
  return { gallons, cost, legsOverRange };
}

/** True when this leg's driving time pushes past the long-drive threshold. */
export function isLongLeg(minutes: number): boolean {
  return minutes > 0 && minutes >= LONG_LEG_MINUTES;
}

/** Total trip distance — sum of haversine between consecutive stops. */
export function totalTripMiles(plan: Pick<ITripPlan, 'stops'>): number {
  let total = 0;
  for (let i = 0; i < plan.stops.length - 1; i++) {
    total += haversineMiles(plan.stops[i], plan.stops[i + 1]);
  }
  return Math.round(total);
}

/** Nearest-neighbor reorder of the middle stops, keeping first and last pinned. */
export function nearestNeighborMiddle(stops: ITripStop[]): ITripStop[] {
  if (stops.length <= 3) return stops.slice();
  const first = stops[0];
  const last = stops[stops.length - 1];
  const middle = stops.slice(1, -1);
  const ordered: ITripStop[] = [];
  let cursor: { lat: number; lng: number } = first;
  while (middle.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < middle.length; i++) {
      const d = haversineMiles(cursor, middle[i]);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    const next = middle.splice(bestIdx, 1)[0];
    ordered.push(next);
    cursor = next;
  }
  return [first, ...ordered, last];
}

/** One-time migration from the old startPoint/endPoint shape into the
 *  unified stops list. Idempotent. */
type LegacyShape = { startPoint?: ITripStop; endPoint?: ITripStop };
function migratePlan(raw: ITripPlan & LegacyShape): ITripPlan {
  const stops = Array.isArray(raw.stops) ? raw.stops.slice() : [];
  if (raw.startPoint) stops.unshift(raw.startPoint);
  if (raw.endPoint) stops.push(raw.endPoint);
  const { startPoint: _s, endPoint: _e, ...rest } = raw;
  void _s; void _e;
  return { ...rest, stops };
}

/** Debounce delay before a changed plan is pushed to the backend — long
 *  enough that a burst of drag-reorder/date-edit calls collapses into one
 *  request instead of one per keystroke. */
const SYNC_DEBOUNCE_MS = 1500;

@Injectable({ providedIn: 'root' })
export class TripPlannerService {
  private platformId = inject(PLATFORM_ID);
  private authService = inject(AuthService);
  private backend = inject(TripBackendService);

  /** Pending debounced sync timers, keyed by plan id. */
  private readonly syncTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private readonly plansStore = createIdbKvStore<ITripPlan[]>({
    legacyKeys: { [TRIP_PLANS_IDB_KEY]: TRIP_PLANS_KEY },
  });
  private readonly activeIdStore = createIdbKvStore<string>({
    legacyKeys: { [TRIP_ACTIVE_ID_IDB_KEY]: TRIP_ACTIVE_ID_KEY },
  });

  /** Resolves once the IDB store has hydrated `_plans$` (immediately off-browser). */
  private hydrated: Promise<void> = Promise.resolve();

  private readonly _plans$ = new BehaviorSubject<ITripPlan[]>([]);
  readonly plans$: Observable<ITripPlan[]> = this._plans$.asObservable();

  constructor() {
    // read() below likely returns [] pre-hydration (store cache starts
    // empty until init() resolves) — do NOT write that back yet. Writing
    // an empty array here would race the store's own legacy-migration
    // transaction and, on the very next load, could permanently overwrite
    // real persisted plans with []. Only persist the migrated shape back
    // once init() has resolved and read() reflects genuine data.
    this._plans$.next(this.read());
    if (isPlatformBrowser(this.platformId)) {
      this.hydrated = this.plansStore.init().then(() => {
        const hydrated = this.read();
        this._plans$.next(hydrated);
        this.write(hydrated, /* skipBus */ true);
        if (this.authService.currentUser) this.syncUnsynced();
      });
      void this.activeIdStore.init();
      // Sign-in (or session restore on load) — push any plan built while
      // signed out, which scheduleSync skipped at the time.
      this.authService.currentUser$
        .pipe(distinctUntilChanged((a, b) => a?.id === b?.id), filter(user => !!user))
        .subscribe(() => this.syncUnsynced());
      this.authService.loggedOut$.subscribe(() => {
        for (const timer of this.syncTimers.values()) clearTimeout(timer);
        this.syncTimers.clear();
        this.write([]);
        this.setActiveId(null);
      });
    }
  }

  list(): ITripPlan[] { return this._plans$.value; }

  get(id: string): ITripPlan | null {
    return this._plans$.value.find(p => p.id === id) ?? null;
  }

  /** Find a plan by its `/trip-planner/:id` segment — local id, or backend id. */
  findByRouteId(routeId: string): ITripPlan | null {
    const remoteId = /^\d+$/.test(routeId) ? Number(routeId) : null;
    return this._plans$.value.find(p => p.id === routeId || (remoteId != null && p.remoteId === remoteId)) ?? null;
  }

  /**
   * Resolve a `/trip-planner/:id` segment, fetching the trip from the backend
   * when it's a backend id this browser doesn't have yet (the owner opening
   * the link on another device). Returns null for a guest, a trip they don't
   * own, or a failed request.
   */
  async loadByRouteId(routeId: string): Promise<ITripPlan | null> {
    await this.hydrated;
    const local = this.findByRouteId(routeId);
    if (local || !/^\d+$/.test(routeId) || !isPlatformBrowser(this.platformId)) return local;
    if (!(await this.authService.currentUserOnceLoaded())) return null;

    let trip: ITrip | null;
    try {
      trip = await firstValueFrom(this.backend.get$(Number(routeId)));
    } catch {
      return null;
    }
    if (!trip?.id) return null;

    // A pull/merge may have added it while the request was in flight.
    const existing = this.findByRouteId(routeId);
    if (existing) return existing;
    const plan = this.materializeRemote(trip);
    this.write([...this._plans$.value, plan]);
    return plan;
  }

  create(name: string): ITripPlan {
    const now = new Date().toISOString();
    const plan: ITripPlan = {
      id: newId('tp'),
      name: name?.trim() || 'Untitled trip',
      stops: [],
      corridorMiles: 0,
      createdAt: now,
      updatedAt: now,
    };
    const all = this._plans$.value.slice();
    all.push(plan);
    this.write(all);
    this.setActiveId(plan.id);
    this.scheduleSync(plan.id);
    return plan;
  }

  update(id: string, patch: Partial<Omit<ITripPlan, 'id' | 'createdAt'>>): ITripPlan | null {
    const all = this._plans$.value.slice();
    const idx = all.findIndex(p => p.id === id);
    if (idx === -1) return null;
    all[idx] = { ...all[idx], ...patch, id: all[idx].id, createdAt: all[idx].createdAt, updatedAt: new Date().toISOString() };
    this.write(all);
    this.scheduleSync(id);
    return all[idx];
  }

  /** Clone an existing plan with fresh ids — stop list, dates, corridor are
   *  all carried over; the name gets a "(copy)" suffix. */
  duplicate(id: string): ITripPlan | null {
    const original = this.get(id);
    if (!original) return null;
    const now = new Date().toISOString();
    const copy: ITripPlan = {
      ...original,
      id: newId('tp'),
      name: `${original.name} (copy)`,
      stops: original.stops.map(s => ({ ...s, id: newId('s') })),
      createdAt: now,
      updatedAt: now,
    };
    const all = this._plans$.value.slice();
    all.push(copy);
    this.write(all);
    this.setActiveId(copy.id);
    this.scheduleSync(copy.id);
    return copy;
  }

  /** Materialize a decoded share payload into a new plan in localStorage and
   *  mark it active. Returns the new plan id. */
  importShare(payload: ITripShareV1): ITripPlan {
    const now = new Date().toISOString();
    const plan: ITripPlan = {
      id: newId('tp'),
      name: payload.n || 'Shared trip',
      startDate: payload.s,
      endDate: payload.e,
      corridorMiles: payload.c ?? 0,
      stops: payload.S.map(s => ({
        id: newId('s'),
        kind: s.k,
        refId: s.r,
        name: s.n,
        lat: s.a,
        lng: s.g,
        address: s.d,
        checkInDate: s.i,
        checkOutDate: s.o,
        notes: s.t,
      })),
      createdAt: now,
      updatedAt: now,
    };
    const all = this._plans$.value.slice();
    all.push(plan);
    this.write(all);
    this.setActiveId(plan.id);
    this.scheduleSync(plan.id);
    return plan;
  }

  delete(id: string): void {
    const plan = this.get(id);
    const next = this._plans$.value.filter(p => p.id !== id);
    this.write(next);
    if (this.getActiveId() === id) this.setActiveId(null);

    const pending = this.syncTimers.get(id);
    if (pending) {
      clearTimeout(pending);
      this.syncTimers.delete(id);
    }
    if (plan?.remoteId && this.authService.currentUser) {
      this.backend.delete$(plan.remoteId).subscribe({ error: () => { /* best-effort */ } });
    }
  }

  /** Appends by default. Pass `insertAt` to insert at a specific position
   *  instead (e.g. between two existing stops) — clamped to the valid range. */
  addStop(planId: string, stop: Omit<ITripStop, 'id'>, insertAt?: number): ITripPlan | null {
    const plan = this.get(planId);
    if (!plan) return null;
    const withId: ITripStop = {
      ...stop,
      id: newId('s'),
      lat: typeof stop.lat === 'string' ? parseFloat(stop.lat) : stop.lat,
      lng: typeof stop.lng === 'string' ? parseFloat(stop.lng) : stop.lng,
    };
    const stops = plan.stops.slice();
    const idx = insertAt == null ? stops.length : Math.max(0, Math.min(insertAt, stops.length));
    stops.splice(idx, 0, withId);
    return this.update(planId, { stops });
  }

  removeStop(planId: string, stopId: string): ITripPlan | null {
    const plan = this.get(planId);
    if (!plan) return null;
    return this.update(planId, { stops: plan.stops.filter(s => s.id !== stopId) });
  }

  /** Patch one stop in-place — for per-stop dates, notes, name edits. */
  updateStop(planId: string, stopId: string, patch: Partial<Omit<ITripStop, 'id'>>): ITripPlan | null {
    const plan = this.get(planId);
    if (!plan) return null;
    const stops = plan.stops.map(s => s.id === stopId ? { ...s, ...patch } : s);
    return this.update(planId, { stops });
  }

  /** Move a stop within the ordered list. CDK drag conventions. */
  reorderStops(planId: string, fromIndex: number, toIndex: number): ITripPlan | null {
    const plan = this.get(planId);
    if (!plan) return null;
    const next = plan.stops.slice();
    if (fromIndex < 0 || fromIndex >= next.length) return plan;
    const clampedTo = Math.max(0, Math.min(next.length - 1, toIndex));
    const [moved] = next.splice(fromIndex, 1);
    next.splice(clampedTo, 0, moved);
    return this.update(planId, { stops: next });
  }

  /** Nearest-neighbor optimization on the middle stops; first + last are pinned. */
  optimizeRoute(planId: string): ITripPlan | null {
    const plan = this.get(planId);
    if (!plan || plan.stops.length <= 3) return plan;
    return this.update(planId, { stops: nearestNeighborMiddle(plan.stops) });
  }

  /**
   * Pulls the signed-in user's trips from the real backend and adds any the
   * local store doesn't already know about (matched by `remoteId`). Local
   * plans always win — this never overwrites an already-linked plan, just
   * fills in ones that only exist on another device/session. Best-effort:
   * silently does nothing for a guest, offline, or a failed request.
   */
  async pullAndMergeFromBackend(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.authService.currentUser) return;

    let remoteTrips: ITrip[];
    try {
      remoteTrips = await firstValueFrom(this.backend.list$());
    } catch {
      return;
    }

    const current = this._plans$.value;
    const knownRemoteIds = new Set(current.map(p => p.remoteId).filter((id): id is number => id != null));
    const now = new Date().toISOString();
    const additions: ITripPlan[] = remoteTrips
      .filter(trip => trip.id != null && !knownRemoteIds.has(trip.id))
      .map(trip => this.materializeRemote(trip, now));

    if (additions.length > 0) {
      this.write([...current, ...additions]);
    }
    this.syncUnsynced();
  }

  /** A backend trip as a new local plan, with fresh local plan/stop ids. */
  private materializeRemote(trip: ITrip, now = new Date().toISOString()): ITripPlan {
    const mapped = apiTripToPlan(trip);
    return {
      ...mapped,
      id: newId('tp'),
      stops: mapped.stops.map(s => ({ ...s, id: newId('s') })),
      createdAt: now,
      updatedAt: now,
    };
  }

  /** Queue a push for every plan that has never reached the backend. */
  private syncUnsynced(): void {
    for (const plan of this._plans$.value) {
      if (plan.remoteId == null) this.scheduleSync(plan.id);
    }
  }

  /** Debounced best-effort push of one plan to the backend. Silently skips
   *  guests and swallows failures — the next local mutation will retry. */
  private scheduleSync(planId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const existing = this.syncTimers.get(planId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.syncTimers.delete(planId);
      void this.syncPlan(planId);
    }, SYNC_DEBOUNCE_MS);
    this.syncTimers.set(planId, timer);
  }

  private async syncPlan(planId: string): Promise<void> {
    if (!(await this.authService.currentUserOnceLoaded())) return;
    const plan = this.get(planId);
    if (!plan) return;

    let saved: ITrip | null;
    try {
      saved = await firstValueFrom(this.backend.save$(plan));
    } catch (err) {
      const gone = err instanceof HttpErrorResponse && err.status === 404;
      if (!plan.remoteId || !gone) return;
      saved = null;
    }

    // The linked trip no longer exists server-side (deleted on another
    // device, or a re-seeded dev DB) — drop the stale link and re-create it,
    // otherwise every later edit keeps PUTting to a dead id.
    if (!saved?.id && plan.remoteId) {
      try {
        saved = await firstValueFrom(this.backend.save$({ ...plan, remoteId: undefined }));
      } catch {
        return;
      }
    }

    if (saved?.id && saved.id !== plan.remoteId) {
      const all = this._plans$.value.slice();
      const idx = all.findIndex(p => p.id === planId);
      if (idx !== -1) {
        all[idx] = { ...all[idx], remoteId: saved.id };
        this.write(all);
      }
    }
  }

  /** Last-edited plan id — drives the "resume" CTA on the list page. */
  getActiveId(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return this.activeIdStore.read(TRIP_ACTIVE_ID_IDB_KEY) ?? null;
  }
  setActiveId(id: string | null): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (id) void this.activeIdStore.write(TRIP_ACTIVE_ID_IDB_KEY, id);
    else void this.activeIdStore.delete(TRIP_ACTIVE_ID_IDB_KEY);
  }

  /** Re-applies `migratePlan()`'s shape fixup on every call (not just once
   *  at construction) — the store's own legacy-localStorage migration
   *  copies old-shape data into IDB as-is, with no knowledge of this
   *  domain's shape evolution, so whatever comes out of the store still
   *  needs this normalization pass, same as the original raw-JSON path did. */
  private read(): ITripPlan[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    const raw = this.plansStore.read(TRIP_PLANS_IDB_KEY) ?? [];
    return raw.map((p: ITripPlan & LegacyShape) => migratePlan(p));
  }

  private write(plans: ITripPlan[], skipBus = false): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.plansStore.write(TRIP_PLANS_IDB_KEY, plans);
    }
    if (!skipBus) this._plans$.next(plans);
  }
}
